import React, { useState, useEffect, useMemo } from "react";
import { useActiveAccount, useActiveWallet } from "thirdweb/react";
import { ThirdwebWalletConnect } from "../components/ThirdwebWalletConnect";
// ABI will be loaded dynamically
import { logger } from "../utils/logger";
import { useHttp } from "../hooks/useHttp";
import { useNavigate } from "react-router-dom";
import { ethers } from "ethers";
import { getEventsRecursive } from "../utils/ethEvents";

interface NFT {
  tokenId: string;
  name: string;
  image: string;
  uid?: string;
  toyType?: number;
  secretLink?: string;
}
const SOCIAL_LINKS = {
  JOIN: "https://paa.ge/chimpersofficial/en",
  REELS:
    "https://www.instagram.com/reel/DRxEAuEjDrd/?igsh=MXB2eGR4czN4eXFqZg==",
} as const;

const Chimpers = () => {
  const navigate = useNavigate();
  const wallet = useActiveWallet();

  const account = useActiveAccount();
  const address = account?.address;
  const eoaAccount = useMemo(() => {
    return (
      (wallet as any)?.getAdminAccount?.() ||
      (wallet as any)?.getAccount?.() ||
      account
    );
  }, [wallet, account]);
  const eoaAddress = eoaAccount?.address;
  const { sendRequest } = useHttp();
  // Contract address and ABI
  const contractAddress = process.env.REACT_APP_CONTRACT_ADDRESS;

  const [isSigning, setIsSigning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nfts, setNfts] = useState<NFT[]>([]);
  const [revealedLinks, setRevealedLinks] = useState<Record<string, string>>(
    {}
  );

  const [showAllNFTs, setShowAllNFTs] = useState(false);

  useEffect(() => {
    console.log(`ClaimAdditional page loaded`, { hasAddress: !!eoaAddress });

    if (eoaAddress) {
      logger.contract(`Loading user Digital Collectibles for address`, {
        address: eoaAddress,
      });
      loadUserNFTs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eoaAddress]);

  const loadUserNFTs = async () => {
    if (!eoaAddress) return;

    logger.contract(`Starting to load user Digital Collectibles`, {
      address: eoaAddress,
      contractAddress,
    });
    setIsLoading(true);
    setError(null);

    try {
      // Load ABI dynamically from public directory
      const response = await fetch("/NFC223Abi.json");
      const NFC223Abi = await response.json();
      logger.contract(`ABI loaded successfully`);

      if (!contractAddress) {
        logger.contractError(
          "Contract address is not configured",
          new Error("Missing REACT_APP_CONTRACT_ADDRESS")
        );
        setError("Contract address is not configured.");
        return;
      }

      // Create a read-only provider for the active chain
      const rpcUrl = process.env.REACT_APP_RPC_URL;
      const provider = new ethers.providers.JsonRpcProvider(rpcUrl);

      const contract = new ethers.Contract(
        contractAddress,
        NFC223Abi,
        provider
      );
      const eoaAccount =
        (wallet as any)?.getAdminAccount?.() ||
        (wallet as any)?.getAccount?.() ||
        account;
      const eoaAddress = eoaAccount?.address;
      console.log("eoaAddress", eoaAddress);

      // Get balance from smart contract
      const balance = await contract.balanceOf(eoaAddress);
      const nftCount = Number(balance);
      logger.contract(`Digital Collectible balance retrieved`, {
        eoaAddress,
        balance: nftCount,
      });

      if (nftCount === 0) {
        logger.contract(`No Digital Collectibles found for address`, {
          eoaAddress,
        });
        setNfts([]);
        return;
      }

      // Read Transfer events using recursive paged query and resolve current ownership quickly
      const addressLower = eoaAddress.toLowerCase();
      const START_BLOCK_ENV = process.env.REACT_APP_CONTRACT_DEPLOY_BLOCK;
      if (!START_BLOCK_ENV) {
        throw new Error("REACT_APP_CONTRACT_DEPLOY_BLOCK is not configured");
      }
      const START_BLOCK = parseInt(START_BLOCK_ENV, 10);

      const logsProvider = provider;
      const ethContract = new ethers.Contract(
        contractAddress,
        NFC223Abi,
        logsProvider
      );
      const [toEvents, fromEvents] = await Promise.all([
        getEventsRecursive({
          contract: ethContract,
          eventName: "Transfer",
          args: [null, eoaAddress],
          fromBlock: START_BLOCK,
        }),
        getEventsRecursive({
          contract: ethContract,
          eventName: "Transfer",
          args: [eoaAddress, null],
          fromBlock: START_BLOCK,
        }),
      ]);

      const combined = [...toEvents, ...fromEvents];
      combined.sort((a, b) => {
        if (a.blockNumber !== b.blockNumber)
          return b.blockNumber - a.blockNumber;
        return (b.logIndex ?? 0) - (a.logIndex ?? 0);
      });

      const ownedTokenIds: string[] = [];
      const seenTokenIds = new Set<string>();
      for (const ev of combined) {
        const args: any = ev.args;
        if (!args) continue;
        const tokenIdStr = args.tokenId?.toString?.();
        if (!tokenIdStr || seenTokenIds.has(tokenIdStr)) continue;
        seenTokenIds.add(tokenIdStr);
        const to = String(args.to).toLowerCase();
        if (to === addressLower) {
          ownedTokenIds.push(tokenIdStr);
          if (ownedTokenIds.length >= nftCount) break;
        }
      }

      const currentlyOwnedTokenIds = ownedTokenIds;

      logger.contract(`Owned tokenIds resolved from events`, {
        resolved: currentlyOwnedTokenIds.length,
        expected: nftCount,
        tokenIds: currentlyOwnedTokenIds,
      });

      // Fallback: if we couldn't resolve enough via events, verify with ownerOf for unresolved extras
      // (kept minimal to avoid heavy scans)
      if (currentlyOwnedTokenIds.length < nftCount) {
        logger.contract(`Resolving remaining tokens via balance check gaps`, {
          missing: nftCount - currentlyOwnedTokenIds.length,
        });
      }

      const userNFTs: NFT[] = [];
      for (const tokenIdStr of currentlyOwnedTokenIds) {
        const toyType = await contract.tokenIdToToyType(tokenIdStr);
        userNFTs.push({
          tokenId: tokenIdStr,
          name: `223 NFC #${tokenIdStr}`,
          image: `https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=300&h=300&fit=crop&crop=center&sig=${tokenIdStr}`,
          toyType: Number(toyType),
        });
      }

      logger.contract(`Digital Collectible loading completed`, {
        totalFound: userNFTs.length,
        expected: nftCount,
        nfts: userNFTs.map((nft) => ({
          tokenId: nft.tokenId,
          uid: nft.uid,
          toyType: nft.toyType,
        })),
      });

      setNfts(userNFTs);
    } catch (err) {
      logger.contractError(
        "Failed to load user Digital Collectibles",
        err as Error,
        {
          address: eoaAddress,
          contractAddress,
        }
      );
      setError("Failed to load Digital Collectibles");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevealRewards = async () => {
    if (!eoaAddress || !eoaAccount) return;

    logger.api(`Starting reveal rewards process`, {
      address: eoaAddress,
      nftCount: nfts.length,
    });
    setIsSigning(true);
    setError(null);

    try {
      const message = "Validate ownership of 223 Digital Collectibles";
      logger.api(`Requesting signature for message`, {
        message,
        address: eoaAddress,
      });

      const signature = await eoaAccount.signMessage({ message });
      logger.api(`Signature received`, {
        signatureLength: signature.length,
        address: eoaAddress,
      });

      // Send to backend to get secret links
      logger.api(`Sending request to backend`, {
        endpoint: "/api/claim-additional",
        address: eoaAddress,
        signatureLength: signature.length,
      });

      const { data } = await sendRequest<{ links: Record<string, string> }>({
        endpoint: "/api/claim-additional",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        payload: { address: eoaAddress, signature },
        responseType: "json",
      });

      const linksObject = (data as any).links || {};

      logger.api(`Secret links received`, {
        linkCount: Object.keys(linksObject).length,
        tokenIds: Object.keys(linksObject),
        address: eoaAddress,
      });

      setRevealedLinks(linksObject);

      navigate("/chimpers-reward", {
        state: {
          revealedLinks: linksObject,
          address: eoaAddress,
        },
      });
    } catch (err) {
      logger.apiError("Failed to reveal rewards", err as Error, {
        address: eoaAddress,
        nftCount: nfts.length,
      });
      setError(err instanceof Error ? err.message : "Failed to reveal rewards");
    } finally {
      setIsSigning(false);
    }
  };

  if (!address && !isLoading) {
    return (
      <div className="flex justify-center items-center bg-gray-50 min-h-[calc(100dvh-149.5px)] p-6">
        <div className="mx-auto max-w-md text-center">
          <div className="p-8 bg-white rounded-lg shadow-md">
            <img
              src="/images/223-logo.png"
              alt="223 Logo"
              className="mx-auto mb-4 max-w-[100px]"
            />

            <h1 className="mb-4 text-2xl font-bold text-gray-900">
              Connect Your Wallet
            </h1>

            <p className="mb-8 text-gray-600">
              Connect your wallet to view your 223 Digital Collectibles and
              reveal additional rewards.
            </p>

            {error && (
              <div className="px-4 py-3 mb-4 text-red-700 bg-red-100 rounded border border-red-400">
                {error}
              </div>
            )}

            <div className="flex justify-center">
              <ThirdwebWalletConnect showDropdown={false} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-6 space-y-5">
        <div className="border border-black rounded-[14px] p-3.5 w-full">
          <div className="flex justify-between items-center">
            <h6 className="font-bold leading-none">My 223 Portal</h6>

            <div className="flex items-center gap-1.5">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.3333 9.33333C11.5101 9.33333 11.6797 9.2631 11.8047 9.13807C11.9298 9.01305 12 8.84348 12 8.66667C12 8.48986 11.9298 8.32029 11.8047 8.19526C11.6797 8.07024 11.5101 8 11.3333 8C11.1565 8 10.987 8.07024 10.8619 8.19526C10.7369 8.32029 10.6667 8.48986 10.6667 8.66667C10.6667 8.84348 10.7369 9.01305 10.8619 9.13807C10.987 9.2631 11.1565 9.33333 11.3333 9.33333ZM11.3333 12C11.5101 12 11.6797 11.9298 11.8047 11.8047C11.9298 11.6797 12 11.5101 12 11.3333C12 11.1565 11.9298 10.987 11.8047 10.8619C11.6797 10.7369 11.5101 10.6667 11.3333 10.6667C11.1565 10.6667 10.987 10.7369 10.8619 10.8619C10.7369 10.987 10.6667 11.1565 10.6667 11.3333C10.6667 11.5101 10.7369 11.6797 10.8619 11.8047C10.987 11.9298 11.1565 12 11.3333 12ZM8.66667 8.66667C8.66667 8.84348 8.59643 9.01305 8.4714 9.13807C8.34638 9.2631 8.17681 9.33333 8 9.33333C7.82319 9.33333 7.65362 9.2631 7.5286 9.13807C7.40357 9.01305 7.33333 8.84348 7.33333 8.66667C7.33333 8.48986 7.40357 8.32029 7.5286 8.19526C7.65362 8.07024 7.82319 8 8 8C8.17681 8 8.34638 8.07024 8.4714 8.19526C8.59643 8.32029 8.66667 8.48986 8.66667 8.66667ZM8.66667 11.3333C8.66667 11.5101 8.59643 11.6797 8.4714 11.8047C8.34638 11.9298 8.17681 12 8 12C7.82319 12 7.65362 11.9298 7.5286 11.8047C7.40357 11.6797 7.33333 11.5101 7.33333 11.3333C7.33333 11.1565 7.40357 10.987 7.5286 10.8619C7.65362 10.7369 7.82319 10.6667 8 10.6667C8.17681 10.6667 8.34638 10.7369 8.4714 10.8619C8.59643 10.987 8.66667 11.1565 8.66667 11.3333ZM4.66667 9.33333C4.84348 9.33333 5.01305 9.2631 5.13807 9.13807C5.2631 9.01305 5.33333 8.84348 5.33333 8.66667C5.33333 8.48986 5.2631 8.32029 5.13807 8.19526C5.01305 8.07024 4.84348 8 4.66667 8C4.48986 8 4.32029 8.07024 4.19526 8.19526C4.07024 8.32029 4 8.48986 4 8.66667C4 8.84348 4.07024 9.01305 4.19526 9.13807C4.32029 9.2631 4.48986 9.33333 4.66667 9.33333ZM4.66667 12C4.84348 12 5.01305 11.9298 5.13807 11.8047C5.2631 11.6797 5.33333 11.5101 5.33333 11.3333C5.33333 11.1565 5.2631 10.987 5.13807 10.8619C5.01305 10.7369 4.84348 10.6667 4.66667 10.6667C4.48986 10.6667 4.32029 10.7369 4.19526 10.8619C4.07024 10.987 4 11.1565 4 11.3333C4 11.5101 4.07024 11.6797 4.19526 11.8047C4.32029 11.9298 4.48986 12 4.66667 12Z"
                  fill="black"
                />
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M4.66665 1.16667C4.79926 1.16667 4.92644 1.21935 5.0202 1.31312C5.11397 1.40689 5.16665 1.53406 5.16665 1.66667V2.17534C5.60798 2.16667 6.09398 2.16667 6.62865 2.16667H9.37065C9.90598 2.16667 10.392 2.16667 10.8333 2.17534V1.66667C10.8333 1.53406 10.886 1.40689 10.9798 1.31312C11.0735 1.21935 11.2007 1.16667 11.3333 1.16667C11.4659 1.16667 11.5931 1.21935 11.6869 1.31312C11.7806 1.40689 11.8333 1.53406 11.8333 1.66667V2.21801C12.0067 2.23134 12.1709 2.24823 12.326 2.26867C13.1073 2.37401 13.74 2.59534 14.2393 3.094C14.738 3.59334 14.9593 4.22601 15.0647 5.00734C15.1667 5.76734 15.1667 6.73734 15.1667 7.96267V9.37067C15.1667 10.596 15.1667 11.5667 15.0647 12.326C14.9593 13.1073 14.738 13.74 14.2393 14.2393C13.74 14.738 13.1073 14.9593 12.326 15.0647C11.566 15.1667 10.596 15.1667 9.37065 15.1667H6.62998C5.40465 15.1667 4.43398 15.1667 3.67465 15.0647C2.89332 14.9593 2.26065 14.738 1.76132 14.2393C1.26265 13.74 1.04132 13.1073 0.935984 12.326C0.833984 11.566 0.833984 10.596 0.833984 9.37067V7.96267C0.833984 6.73734 0.833984 5.76667 0.935984 5.00734C1.04132 4.22601 1.26265 3.59334 1.76132 3.094C2.26065 2.59534 2.89332 2.37401 3.67465 2.26867C3.83021 2.24823 3.99443 2.23134 4.16732 2.21801V1.66667C4.16732 1.53418 4.2199 1.4071 4.31353 1.31335C4.40715 1.2196 4.53416 1.16685 4.66665 1.16667ZM3.80665 3.26C3.13665 3.35001 2.74998 3.51934 2.46798 3.80134C2.18598 4.08334 2.01665 4.47001 1.92665 5.14001C1.91154 5.25334 1.89865 5.37312 1.88798 5.49934H14.112C14.1013 5.37312 14.0884 5.25312 14.0733 5.13934C13.9833 4.46934 13.814 4.08267 13.532 3.80067C13.25 3.51867 12.8633 3.34934 12.1927 3.25934C11.508 3.16734 10.6047 3.16601 9.33332 3.16601H6.66665C5.39532 3.16601 4.49265 3.168 3.80665 3.26ZM1.83332 8.00001C1.83332 7.43067 1.83332 6.93534 1.84198 6.50001H14.158C14.1667 6.93534 14.1667 7.43067 14.1667 8.00001V9.33334C14.1667 10.6047 14.1653 11.508 14.0733 12.1933C13.9833 12.8633 13.814 13.25 13.532 13.532C13.25 13.814 12.8633 13.9833 12.1927 14.0733C11.508 14.1653 10.6047 14.1667 9.33332 14.1667H6.66665C5.39532 14.1667 4.49265 14.1653 3.80665 14.0733C3.13665 13.9833 2.74998 13.814 2.46798 13.532C2.18598 13.25 2.01665 12.8633 1.92665 12.1927C1.83465 11.508 1.83332 10.6047 1.83332 9.33334V8.00001Z"
                  fill="black"
                />
              </svg>

              <span className="text-xs">
                {new Date().toLocaleDateString("en-US", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </span>
            </div>
          </div>
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="inline-block w-8 h-8 rounded-full border-b-2 border-gray-900 animate-spin"></div>
              <p className="ml-2 text-gray-600">
                Loading your Digital Collectibles...
              </p>
            </div>
          ) : nfts.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-gray-600">
                You don't own any 223 Digital Collectibles yet.
              </p>
            </div>
          ) : (
            <div className="mt-4">
              {nfts.length === 1 && (
                <div className="relative rounded-[14px] overflow-hidden aspect-[16/9]">
                  <img
                    src={`/images/toys/${(nfts[0].toyType ?? 0) + 1}.png`}
                    alt={nfts[0].name}
                    className="object-cover w-full h-full"
                  />

                  <div className="absolute inset-x-2.5 bottom-2.5 px-2 py-1 border border-white backdrop-blur-md rounded-lg">
                    <h3 className="text-lg font-bold text-white">
                      {nfts[0].name}
                    </h3>

                    <p className="text-[10px] text-white">Claimed</p>
                  </div>
                </div>
              )}

              {nfts.length === 2 && (
                <div className="grid grid-cols-2 gap-3">
                  {nfts.map((nft) => (
                    <div
                      key={nft.tokenId}
                      className="relative rounded-[14px] overflow-hidden aspect-square"
                    >
                      <img
                        src={`/images/toys/${(nft.toyType ?? 0) + 1}.png`}
                        alt={nft.name}
                        className="object-cover w-full h-full"
                      />

                      <div className="absolute inset-x-2.5 bottom-2.5 p-1 border border-white backdrop-blur-md rounded-lg">
                        <h3 className="text-sm font-bold text-white">
                          {nft.name}
                        </h3>

                        <div className="flex gap-1 items-center text-[8px] text-white">
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M6 1C8.7615 1 11 3.2385 11 6C11 8.7615 8.7615 11 6 11C3.2385 11 1 8.7615 1 6C1 3.2385 3.2385 1 6 1ZM6.741 8.47C6.24784 8.51074 5.75216 8.51074 5.259 8.47C5.30567 8.66867 5.35733 8.85233 5.414 9.021C5.5395 9.3985 5.679 9.6675 5.809 9.832C5.8725 9.913 5.924 9.956 5.959 9.977L5.9845 9.991L5.9995 9.995L6.0155 9.991L6.041 9.9775C6.09908 9.9383 6.14985 9.88922 6.191 9.8325C6.321 9.6675 6.4605 9.3985 6.586 9.021C6.64267 8.85233 6.69433 8.66867 6.741 8.47ZM2.4035 7.7525C2.85773 8.68194 3.65317 9.40019 4.624 9.7575C4.5653 9.61972 4.51226 9.47961 4.465 9.3375C4.35618 9.00472 4.26933 8.66516 4.205 8.321C3.58622 8.19522 2.98239 8.00467 2.4035 7.7525ZM9.597 7.7525C9.0245 8.0025 8.4215 8.194 7.7945 8.321C7.73019 8.66533 7.64334 9.00506 7.5345 9.338C7.4865 9.48367 7.4335 9.62333 7.3755 9.757C8.3467 9.39991 9.14254 8.68163 9.597 7.752V7.7525ZM6.988 5.4355C6.33222 5.52265 5.66778 5.52265 5.012 5.4355C4.98343 6.10715 5.007 6.78 5.0825 7.448C5.69219 7.51751 6.30781 7.51751 6.9175 7.448C6.973 6.96734 7.00055 6.48386 7 6C7 5.808 6.996 5.61967 6.988 5.435V5.4355ZM2.284 4.516C2.03999 5.12748 1.95068 5.78973 2.024 6.444C2.66107 6.80885 3.34532 7.08426 4.0575 7.2625C3.99622 6.58878 3.98386 5.91151 4.0205 5.236C3.41376 5.06978 2.83035 4.82788 2.284 4.516ZM9.716 4.516C9.16965 4.82788 8.58624 5.06978 7.9795 5.236C8.01614 5.91151 8.00378 6.58878 7.9425 7.2625C8.65451 7.08422 9.33859 6.8088 9.9755 6.444C10.0487 5.78989 9.95943 5.12782 9.7155 4.5165L9.716 4.516ZM6.008 2.006L6.0025 2.005L5.9925 2.0065L5.9735 2.014C5.9093 2.05429 5.85331 2.10637 5.8085 2.1675C5.6785 2.3325 5.5395 2.6015 5.4135 2.9785C5.2785 3.3825 5.1695 3.8785 5.097 4.438C5.69607 4.52204 6.30393 4.52204 6.903 4.438C6.8305 3.878 6.721 3.3825 6.5865 2.9785C6.4605 2.6015 6.3215 2.332 6.1915 2.1675C6.15035 2.11078 6.09958 2.0617 6.0415 2.0225L6.008 2.006ZM4.624 2.243C3.88093 2.51539 3.23537 3.00209 2.769 3.6415C3.1905 3.8835 3.642 4.0795 4.1165 4.223C4.1965 3.641 4.315 3.1115 4.465 2.662C4.51333 2.516 4.56633 2.37633 4.624 2.243ZM7.376 2.243C7.43333 2.37633 7.48633 2.516 7.535 2.662C7.685 3.112 7.8035 3.641 7.8835 4.223C8.35318 4.08066 8.80523 3.88558 9.231 3.6415C8.76463 3.00209 8.11907 2.51539 7.376 2.243Z"
                              fill="white"
                            />
                          </svg>
                          <span>Toy Type {nft.toyType}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {nfts.length === 3 && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="relative rounded-[14px] overflow-hidden aspect-auto">
                    <img
                      src={`/images/toys/${nfts[0].toyType}.png`}
                      alt={nfts[0].name}
                      className="object-cover w-full h-full"
                    />
                    <div className="absolute inset-x-2.5 bottom-2.5 px-2 py-1 border border-white backdrop-blur-md rounded-lg">
                      <h3 className="text-lg font-bold text-white">
                        {nfts[0].name}
                      </h3>
                      <p className="text-[10px] text-white">Claimed</p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    {nfts.slice(1, 3).map((nft) => (
                      <div
                        key={nft.tokenId}
                        className="relative rounded-[14px] overflow-hidden aspect-[4/3]"
                      >
                        <img
                          src={`/images/toys/${(nft.toyType ?? 0) + 1}.png`}
                          alt={nft.name}
                          className="object-cover w-full h-full"
                        />
                        <div className="absolute inset-x-2.5 bottom-2.5 p-1 border border-white backdrop-blur-md rounded-lg">
                          <h3 className="text-sm font-bold text-white">
                            {nft.name}
                          </h3>
                          <div className="flex gap-1 items-center text-[8px] text-white">
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M6 1C8.7615 1 11 3.2385 11 6C11 8.7615 8.7615 11 6 11C3.2385 11 1 8.7615 1 6C1 3.2385 3.2385 1 6 1ZM6.741 8.47C6.24784 8.51074 5.75216 8.51074 5.259 8.47C5.30567 8.66867 5.35733 8.85233 5.414 9.021C5.5395 9.3985 5.679 9.6675 5.809 9.832C5.8725 9.913 5.924 9.956 5.959 9.977L5.9845 9.991L5.9995 9.995L6.0155 9.991L6.041 9.9775C6.09908 9.9383 6.14985 9.88922 6.191 9.8325C6.321 9.6675 6.4605 9.3985 6.586 9.021C6.64267 8.85233 6.69433 8.66867 6.741 8.47ZM2.4035 7.7525C2.85773 8.68194 3.65317 9.40019 4.624 9.7575C4.5653 9.61972 4.51226 9.47961 4.465 9.3375C4.35618 9.00472 4.26933 8.66516 4.205 8.321C3.58622 8.19522 2.98239 8.00467 2.4035 7.7525ZM9.597 7.7525C9.0245 8.0025 8.4215 8.194 7.7945 8.321C7.73019 8.66533 7.64334 9.00506 7.5345 9.338C7.4865 9.48367 7.4335 9.62333 7.3755 9.757C8.3467 9.39991 9.14254 8.68163 9.597 7.752V7.7525ZM6.988 5.4355C6.33222 5.52265 5.66778 5.52265 5.012 5.4355C4.98343 6.10715 5.007 6.78 5.0825 7.448C5.69219 7.51751 6.30781 7.51751 6.9175 7.448C6.973 6.96734 7.00055 6.48386 7 6C7 5.808 6.996 5.61967 6.988 5.435V5.4355ZM2.284 4.516C2.03999 5.12748 1.95068 5.78973 2.024 6.444C2.66107 6.80885 3.34532 7.08426 4.0575 7.2625C3.99622 6.58878 3.98386 5.91151 4.0205 5.236C3.41376 5.06978 2.83035 4.82788 2.284 4.516ZM9.716 4.516C9.16965 4.82788 8.58624 5.06978 7.9795 5.236C8.01614 5.91151 8.00378 6.58878 7.9425 7.2625C8.65451 7.08422 9.33859 6.8088 9.9755 6.444C10.0487 5.78989 9.95943 5.12782 9.7155 4.5165L9.716 4.516ZM6.008 2.006L6.0025 2.005L5.9925 2.0065L5.9735 2.014C5.9093 2.05429 5.85331 2.10637 5.8085 2.1675C5.6785 2.3325 5.5395 2.6015 5.4135 2.9785C5.2785 3.3825 5.1695 3.8785 5.097 4.438C5.69607 4.52204 6.30393 4.52204 6.903 4.438C6.8305 3.878 6.721 3.3825 6.5865 2.9785C6.4605 2.6015 6.3215 2.332 6.1915 2.1675C6.15035 2.11078 6.09958 2.0617 6.0415 2.0225L6.008 2.006ZM4.624 2.243C3.88093 2.51539 3.23537 3.00209 2.769 3.6415C3.1905 3.8835 3.642 4.0795 4.1165 4.223C4.1965 3.641 4.315 3.1115 4.465 2.662C4.51333 2.516 4.56633 2.37633 4.624 2.243ZM7.376 2.243C7.43333 2.37633 7.48633 2.516 7.535 2.662C7.685 3.112 7.8035 3.641 7.8835 4.223C8.35318 4.08066 8.80523 3.88558 9.231 3.6415C8.76463 3.00209 8.11907 2.51539 7.376 2.243Z"
                                fill="white"
                              />
                            </svg>
                            <span>Toy Type {nft.toyType}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {nfts.length >= 4 && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    {(showAllNFTs ? nfts : nfts.slice(0, 4)).map((nft) => (
                      <div
                        key={nft.tokenId}
                        className="relative rounded-[14px] overflow-hidden aspect-square"
                      >
                        <img
                          src={`/images/toys/${nft.toyType}.png`}
                          alt={nft.name}
                          className="object-cover w-full h-full"
                        />
                        <div className="absolute inset-x-2.5 bottom-2.5 p-1 border border-white backdrop-blur-md rounded-lg">
                          <h3 className="text-sm font-bold text-white">
                            {nft.name}
                          </h3>
                          <div className="flex gap-1 items-center text-[8px] text-white">
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M6 1C8.7615 1 11 3.2385 11 6C11 8.7615 8.7615 11 6 11C3.2385 11 1 8.7615 1 6C1 3.2385 3.2385 1 6 1ZM6.741 8.47C6.24784 8.51074 5.75216 8.51074 5.259 8.47C5.30567 8.66867 5.35733 8.85233 5.414 9.021C5.5395 9.3985 5.679 9.6675 5.809 9.832C5.8725 9.913 5.924 9.956 5.959 9.977L5.9845 9.991L5.9995 9.995L6.0155 9.991L6.041 9.9775C6.09908 9.9383 6.14985 9.88922 6.191 9.8325C6.321 9.6675 6.4605 9.3985 6.586 9.021C6.64267 8.85233 6.69433 8.66867 6.741 8.47ZM2.4035 7.7525C2.85773 8.68194 3.65317 9.40019 4.624 9.7575C4.5653 9.61972 4.51226 9.47961 4.465 9.3375C4.35618 9.00472 4.26933 8.66516 4.205 8.321C3.58622 8.19522 2.98239 8.00467 2.4035 7.7525ZM9.597 7.7525C9.0245 8.0025 8.4215 8.194 7.7945 8.321C7.73019 8.66533 7.64334 9.00506 7.5345 9.338C7.4865 9.48367 7.4335 9.62333 7.3755 9.757C8.3467 9.39991 9.14254 8.68163 9.597 7.752V7.7525ZM6.988 5.4355C6.33222 5.52265 5.66778 5.52265 5.012 5.4355C4.98343 6.10715 5.007 6.78 5.0825 7.448C5.69219 7.51751 6.30781 7.51751 6.9175 7.448C6.973 6.96734 7.00055 6.48386 7 6C7 5.808 6.996 5.61967 6.988 5.435V5.4355ZM2.284 4.516C2.03999 5.12748 1.95068 5.78973 2.024 6.444C2.66107 6.80885 3.34532 7.08426 4.0575 7.2625C3.99622 6.58878 3.98386 5.91151 4.0205 5.236C3.41376 5.06978 2.83035 4.82788 2.284 4.516ZM9.716 4.516C9.16965 4.82788 8.58624 5.06978 7.9795 5.236C8.01614 5.91151 8.00378 6.58878 7.9425 7.2625C8.65451 7.08422 9.33859 6.8088 9.9755 6.444C10.0487 5.78989 9.95943 5.12782 9.7155 4.5165L9.716 4.516ZM6.008 2.006L6.0025 2.005L5.9925 2.0065L5.9735 2.014C5.9093 2.05429 5.85331 2.10637 5.8085 2.1675C5.6785 2.3325 5.5395 2.6015 5.4135 2.9785C5.2785 3.3825 5.1695 3.8785 5.097 4.438C5.69607 4.52204 6.30393 4.52204 6.903 4.438C6.8305 3.878 6.721 3.3825 6.5865 2.9785C6.4605 2.6015 6.3215 2.332 6.1915 2.1675C6.15035 2.11078 6.09958 2.0617 6.0415 2.0225L6.008 2.006ZM4.624 2.243C3.88093 2.51539 3.23537 3.00209 2.769 3.6415C3.1905 3.8835 3.642 4.0795 4.1165 4.223C4.1965 3.641 4.315 3.1115 4.465 2.662C4.51333 2.516 4.56633 2.37633 4.624 2.243ZM7.376 2.243C7.43333 2.37633 7.48633 2.516 7.535 2.662C7.685 3.112 7.8035 3.641 7.8835 4.223C8.35318 4.08066 8.80523 3.88558 9.231 3.6415C8.76463 3.00209 8.11907 2.51539 7.376 2.243Z"
                                fill="white"
                              />
                            </svg>
                            <span>Toy Type {nft.toyType}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {nfts.length > 4 && (
                    <div className="flex justify-center">
                      <button
                        onClick={() => setShowAllNFTs(!showAllNFTs)}
                        className="px-6 py-2 text-sm font-semibold text-black bg-white border-2 border-black rounded-[50px] hover:bg-black hover:text-white transition-colors"
                      >
                        {showAllNFTs
                          ? `Show Less`
                          : `Show All (${nfts.length})`}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {error && (
          <div className="px-4 py-3 mb-4 text-red-700 bg-red-100 rounded border border-red-400">
            {error}
          </div>
        )}

        <div
          className="bg-gradient-to-r from-[#CC0054] to-[#CC00AA] p-4 rounded-[10px] px-[29.5px] py-3.5 font-bold sm:text-lg text-base text-white"
          onClick={() => navigate("/chimpers-wallpaper")}
        >
          Download your Chimpers wallpapers
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div
            className="relative rounded-[10px] overflow-hidden aspect-square bg-gradient-to-br from-[#DA4453CC] to-[#89216BCC] flex flex-col justify-between cursor-pointer"
            onClick={() =>
              window.open(SOCIAL_LINKS.JOIN, "_blank", "noopener,noreferrer")
            }
          >
            <img
              src="/images/join-us.png"
              alt="Join Us"
              className="object-cover h-full"
            />
          </div>

          <div
            className="relative rounded-[10px] overflow-hidden aspect-square bg-gradient-to-br from-[#2C3E50] to-[#4CA1AF] flex items-center justify-center cursor-pointer"
            onClick={() =>
              window.open(SOCIAL_LINKS.REELS, "_blank", "noopener,noreferrer")
            }
          >
            <img
              src="/images/reels.png"
              alt="Watch Reels"
              className="object-cover h-full"
            />
          </div>
        </div>

        <div className="relative aspect-video">
          <img
            src="/images/chimpers.png"
            alt="Monkey Group"
            className="object-cover rounded-[15px] h-full"
          />

          <div className="flex absolute right-0 left-0 bottom-2 justify-center">
            <button
              onClick={handleRevealRewards}
              disabled={isSigning || Object.keys(revealedLinks).length > 0}
              className="text-sm font-bold bg-[#343434] text-white py-2 px-3 rounded-[50px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSigning
                ? "Signing..."
                : Object.keys(revealedLinks).length > 0
                ? "Rewards Revealed"
                : "Claim Chimpers Rewards"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Chimpers;
